import { useEffect, useState, useRef } from "react";
import { api, formatApiError, formatRelative } from "@/lib/api";
import { toast } from "sonner";
import {
  Wrench, Play, RefreshCw, AlertTriangle, ShieldAlert,
  CheckCircle2, XCircle, Loader2, Clock, X, Ban, CalendarClock, Pause, Mail, MailX, Users as UsersIcon,
} from "lucide-react";

const DANGER_TONE = {
  safe:        { bg: "bg-[#DCFCE7]", text: "text-[#166534]", label: "Safe · Idempotent" },
  caution:     { bg: "bg-[#FEF3C7]", text: "text-[#92400E]", label: "Caution" },
  destructive: { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", label: "Destructive" },
};

const STATUS_TONE = {
  queued:    { bg: "bg-[#E0E7FF]", text: "text-[#1E40AF]", Icon: Clock,        label: "Queued" },
  running:   { bg: "bg-[#DBEAFE]", text: "text-[#1E3A8A]", Icon: Loader2,      label: "Running", spin: true },
  success:   { bg: "bg-[#DCFCE7]", text: "text-[#166534]", Icon: CheckCircle2, label: "Success" },
  failed:    { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", Icon: XCircle,      label: "Failed" },
  cancelled: { bg: "bg-[#F1F5F9]", text: "text-[#334155]", Icon: Ban,          label: "Cancelled" },
};

export default function AdminMaintenance() {
  const [jobs, setJobs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingJob, setPendingJob] = useState(null);
  const [openRunId, setOpenRunId] = useState(null);
  const [recipientsOpen, setRecipientsOpen] = useState(false);

  const loadAll = async () => {
    try {
      const [jobsRes, runsRes] = await Promise.all([
        api.get("/admin/maintenance/jobs"),
        api.get("/admin/maintenance/runs", { params: { limit: 50 } }),
      ]);
      setJobs(jobsRes.data.jobs || []);
      setRuns(runsRes.data.runs || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // Re-poll periodically while there's an in-flight run so the table
    // refreshes itself without manual reload.
    const t = setInterval(() => {
      if (runs.some((r) => r.status === "queued" || r.status === "running")) loadAll();
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs.length]);

  const runJob = async (job) => {
    try {
      const { data } = await api.post(`/admin/maintenance/jobs/${job.key}/run`);
      toast.success(`Started · ${job.name}`);
      setPendingJob(null);
      setOpenRunId(data.id);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleSchedule = async (job) => {
    const enabled = !(job.schedule?.enabled);
    try {
      await api.patch(`/admin/maintenance/jobs/${job.key}/schedule`, { enabled });
      toast.success(enabled ? `Schedule resumed · ${job.name}` : `Schedule paused · ${job.name}`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendTestNotification = async (job) => {
    try {
      const { data } = await api.post(`/admin/maintenance/jobs/${job.key}/test-notification`);
      const sent = !!data.sent;
      const to = (data.to || []).length;
      const failed = (data.failed || []).length;
      if (sent && !failed) {
        toast.success(`Test sent to ${to} recipient${to === 1 ? "" : "s"} · ${job.name}`);
      } else if (sent && failed) {
        toast.warning(`Test sent to ${to}, ${failed} bounced · check Resend logs`);
      } else {
        toast.error(data.reason || "Test email failed");
      }
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="admin-maintenance-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="hw-eyebrow mb-2">Operations · maintenance</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Maintenance Jobs</h1>
          <p className="text-sm text-[#334155] mt-1 max-w-3xl">
            One-click execution of administrative repair, backfill, cleanup, reindex, and migration tasks. Every run is gated by RBAC, audit-logged, and re-runnable.
          </p>
        </div>
        <button
          type="button"
          onClick={loadAll}
          data-testid="maintenance-refresh"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-xs font-medium hover:bg-[#F9F8F6]"
        >
          <RefreshCw className="w-3.5 h-3.5" strokeWidth={2} /> Refresh
        </button>
        <button
          type="button"
          onClick={() => setRecipientsOpen(true)}
          data-testid="maintenance-recipients-btn"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-xs font-medium hover:bg-[#F9F8F6]"
          title="Manage who receives maintenance notification emails"
        >
          <UsersIcon className="w-3.5 h-3.5" strokeWidth={2} /> IT recipients
        </button>
      </div>

      <section className="space-y-3" data-testid="maintenance-jobs-section">
        <h2 className="font-display text-lg font-semibold text-[#0F172A]">Available jobs</h2>
        {loading ? (
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="h-32 bg-white border border-[#E5E5E0] rounded-2xl animate-pulse" />
            <div className="h-32 bg-white border border-[#E5E5E0] rounded-2xl animate-pulse" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center text-[#334155]" data-testid="maintenance-no-jobs">
            <Wrench className="w-10 h-10 mx-auto text-[#1E3A8A] opacity-50 mb-2" strokeWidth={1.5} />
            No maintenance jobs registered yet.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {jobs.map((j) => {
              const tone = DANGER_TONE[j.danger_level] || DANGER_TONE.safe;
              const Icon = j.danger_level === "safe" ? Wrench : (j.danger_level === "caution" ? AlertTriangle : ShieldAlert);
              return (
                <article key={j.key} data-testid={`maintenance-job-${j.key}`} className="bg-white border border-[#E5E5E0] rounded-2xl p-4 flex flex-col gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl ${tone.bg} ${tone.text} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-4 h-4" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h3 className="font-medium text-[#0F172A]">{j.name}</h3>
                        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${tone.bg} ${tone.text}`}>{tone.label}</span>
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-[#1E3A8A] mt-0.5">{j.category}</div>
                    </div>
                  </div>
                  {(j.notify_on || []).length > 0 && (
                    <div className="-mt-1 inline-flex items-center gap-1 self-start text-[10px] text-[#1E3A8A]" data-testid={`maintenance-notify-${j.key}`}>
                      <span className="px-1.5 py-0.5 rounded-full bg-[#E0E7FF] uppercase tracking-wider">
                        Notifies IT on {j.notify_on.join(" + ")}
                      </span>
                    </div>
                  )}
                  {j.last_notification && (
                    <LastNotificationPill
                      jobKey={j.key}
                      last={j.last_notification}
                      onOpen={() => setOpenRunId(j.last_notification.run_id)}
                    />
                  )}
                  <p className="text-xs text-[#334155]">{j.description}</p>
                  {j.cron && (
                    <div className="flex items-center justify-between gap-2 -mt-1 px-2.5 py-1.5 rounded-lg bg-[#F9F8F6] border border-[#E5E5E0]">
                      <div className="flex items-center gap-1.5 text-[11px] text-[#1E3A8A] min-w-0">
                        <CalendarClock className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.75} />
                        <span className="font-medium">
                          {j.schedule_description || j.cron}
                        </span>
                        {j.schedule?.enabled === false && (
                          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-[9px] uppercase tracking-wider">Paused</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleSchedule(j)}
                        disabled={!j.can_run}
                        data-testid={`maintenance-schedule-toggle-${j.key}`}
                        title={j.schedule?.enabled === false ? "Resume schedule" : "Pause schedule"}
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-[#E5E5E0] bg-white text-[#1E3A8A] hover:bg-[#E8ECE7] disabled:opacity-50"
                      >
                        {j.schedule?.enabled === false ? (
                          <><Play className="w-2.5 h-2.5" strokeWidth={2.5} /> Resume</>
                        ) : (
                          <><Pause className="w-2.5 h-2.5" strokeWidth={2.5} /> Pause</>
                        )}
                      </button>
                    </div>
                  )}
                  {j.schedule?.next_run_at && j.schedule?.enabled !== false && (
                    <div className="text-[10px] text-[#1E3A8A] -mt-2" data-testid={`maintenance-next-run-${j.key}`}>
                      Next run · {formatRelative(j.schedule.next_run_at)} ({new Date(j.schedule.next_run_at).toLocaleString()})
                    </div>
                  )}
                  <div className="mt-auto pt-2 border-t border-[#F0EFEB] flex justify-end gap-2">
                    {(j.notify_on || []).length > 0 && (
                      <button
                        type="button"
                        onClick={() => sendTestNotification(j)}
                        disabled={!j.can_run}
                        data-testid={`maintenance-test-notify-${j.key}`}
                        title="Send a synthetic [TEST] email through the same pipeline this job uses"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-xs font-medium hover:bg-[#F9F8F6] disabled:opacity-50"
                      >
                        <Mail className="w-3 h-3" strokeWidth={2.5} /> Test email
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setPendingJob(j)}
                      disabled={!j.can_run}
                      data-testid={`maintenance-run-${j.key}`}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium hover:bg-[#172554] disabled:opacity-50"
                      title={j.can_run ? "" : "You don't have permission to run this job."}
                    >
                      <Play className="w-3 h-3" strokeWidth={2.5} /> Run
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="space-y-3" data-testid="maintenance-runs-section">
        <h2 className="font-display text-lg font-semibold text-[#0F172A]">Recent runs</h2>
        {runs.length === 0 ? (
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center text-[#334155]" data-testid="maintenance-no-runs">
            No runs yet.
          </div>
        ) : (
          <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-x-auto">
            <table className="w-full text-sm" data-testid="maintenance-runs-table">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E5E0]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Job</th>
                  <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider hidden md:table-cell">Started</th>
                  <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Started by</th>
                  <th className="text-right px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Duration</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const tone = STATUS_TONE[r.status] || STATUS_TONE.queued;
                  const Icon = tone.Icon;
                  return (
                    <tr key={r.id} data-testid={`maintenance-run-row-${r.id}`} className="border-t border-[#F0EFEB] hover:bg-[#F9F8F6] cursor-pointer"
                        onClick={() => setOpenRunId(r.id)}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#0F172A]">{r.job_name}</div>
                        <div className="text-[10px] text-[#1E3A8A] font-mono">{r.job_key}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tone.bg} ${tone.text}`}>
                          <Icon className={`w-3 h-3 ${tone.spin ? "animate-spin" : ""}`} strokeWidth={2.5} />
                          {tone.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#334155] hidden md:table-cell text-xs">
                        {formatRelative(r.started_at)}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#334155]">
                        {r.started_by?.name || r.started_by?.email || "—"}
                        {r.params?.triggered_by === "schedule" && (
                          <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[#E0E7FF] text-[#1E3A8A] text-[9px] uppercase tracking-wider">Scheduled</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-[#334155]">{r.duration_ms ? `${(r.duration_ms / 1000).toFixed(1)}s` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {pendingJob && (
        <ConfirmRunDialog
          job={pendingJob}
          onCancel={() => setPendingJob(null)}
          onConfirm={() => runJob(pendingJob)}
        />
      )}
      {openRunId && (
        <RunDetailDialog
          runId={openRunId}
          onClose={() => { setOpenRunId(null); loadAll(); }}
        />
      )}
      {recipientsOpen && (
        <ITRecipientsDialog onClose={() => setRecipientsOpen(false)} />
      )}
    </div>
  );
}

function ConfirmRunDialog({ job, onCancel, onConfirm }) {
  const tone = DANGER_TONE[job.danger_level] || DANGER_TONE.safe;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40" data-testid="maintenance-confirm-dialog">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E0]">
          <div className="flex items-center gap-2.5">
            <ShieldAlert className={`w-5 h-5 ${tone.text}`} strokeWidth={1.75} />
            <h3 className="font-display text-lg font-semibold text-[#0F172A]">Confirm: {job.name}</h3>
          </div>
          <button onClick={onCancel} data-testid="maintenance-confirm-close" className="p-2 rounded-full hover:bg-[#F9F8F6]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tone.bg} ${tone.text}`}>{tone.label}</span>
          <p className="text-sm text-[#334155] whitespace-pre-line">{job.confirm_message}</p>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#E5E5E0]">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-[#334155]">Cancel</button>
          <button onClick={onConfirm} data-testid="maintenance-confirm-run" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]">
            <Play className="w-3.5 h-3.5" strokeWidth={2.5} /> Run job
          </button>
        </div>
      </div>
    </div>
  );
}

function RunDetailDialog({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/maintenance/runs/${runId}`);
      setRun(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(async () => {
      const r = await load();
      if (r && r.status !== "queued" && r.status !== "running") {
        clearInterval(pollRef.current);
      }
    }, 1500);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/maintenance/runs/${runId}/cancel`);
      toast.success("Cancel signal sent");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const tone = run ? (STATUS_TONE[run.status] || STATUS_TONE.queued) : STATUS_TONE.queued;
  const Icon = tone.Icon;
  const live = run && (run.status === "queued" || run.status === "running");

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-[#0F172A]/40 overflow-y-auto" data-testid="maintenance-run-dialog">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E0]">
          <div className="flex items-center gap-2.5">
            <Icon className={`w-5 h-5 ${tone.text} ${tone.spin ? "animate-spin" : ""}`} strokeWidth={1.75} />
            <div>
              <h3 className="font-display text-lg font-semibold text-[#0F172A]">{run?.job_name || "…"}</h3>
              <div className="text-[10px] text-[#1E3A8A] font-mono">{runId}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="maintenance-run-close" className="p-2 rounded-full hover:bg-[#F9F8F6]"><X className="w-5 h-5" /></button>
        </div>

        {!run ? (
          <div className="p-8 text-center text-[#1E3A8A]"><Loader2 className="w-5 h-5 animate-spin inline-block" /> Loading…</div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tone.bg} ${tone.text}`}>{tone.label}</span>
              <span className="text-xs text-[#334155]">Started {formatRelative(run.started_at)} by <strong>{run.started_by?.name || run.started_by?.email || "—"}</strong></span>
            </div>

            <div>
              <div className="flex items-center justify-between text-xs text-[#1E3A8A] mb-1">
                <span>Progress</span>
                <span data-testid="maintenance-run-progress-pct">{run.progress_pct || 0}%</span>
              </div>
              <div className="h-2 rounded-full bg-[#F0EFEB] overflow-hidden">
                <div
                  className={`h-full ${run.status === "failed" ? "bg-[#991B1B]" : "bg-[#1E3A8A]"} transition-all`}
                  style={{ width: `${run.progress_pct || 0}%` }}
                />
              </div>
              {run.progress_message && (
                <div className="text-xs text-[#334155] mt-1.5" data-testid="maintenance-run-progress-msg">{run.progress_message}</div>
              )}
            </div>

            {run.result && (
              <details className="rounded-xl border border-[#E5E5E0] bg-[#F9F8F6]" open>
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-[#0F172A]">Result</summary>
                <pre className="px-3 py-2 text-[11px] whitespace-pre-wrap text-[#334155] overflow-x-auto" data-testid="maintenance-run-result">{JSON.stringify(run.result, null, 2)}</pre>
              </details>
            )}
            {run.error && (
              <details className="rounded-xl border border-[#FCA5A5] bg-[#FEE2E2]" open>
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-[#991B1B]">Error</summary>
                <pre className="px-3 py-2 text-[11px] whitespace-pre-wrap text-[#991B1B] overflow-x-auto" data-testid="maintenance-run-error">{run.error}{run.traceback ? `\n\n${run.traceback}` : ""}</pre>
              </details>
            )}

            <NotificationDiagnostic notif={run.notification} />

            <div className="flex items-center justify-between gap-2 pt-2">
              <div className="text-xs text-[#1E3A8A]">
                {run.duration_ms != null && `Took ${(run.duration_ms / 1000).toFixed(2)}s`}
              </div>
              <div className="flex gap-2">
                {live && (
                  <button onClick={cancel} disabled={busy} data-testid="maintenance-run-cancel" className="px-4 py-1.5 text-xs rounded-full bg-white border border-[#FCA5A5] text-[#991B1B] hover:bg-[#FEE2E2] disabled:opacity-60">
                    Cancel run
                  </button>
                )}
                <button onClick={onClose} className="px-4 py-1.5 text-xs rounded-full bg-[#1E3A8A] text-white hover:bg-[#172554]">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function NotificationDiagnostic({ notif }) {
  if (!notif) return null;
  const sent = !!notif.sent;
  const toCount = (notif.to || []).length;
  const failedCount = (notif.failed || []).length;
  const triggers = (notif.notify_on || []).join(" + ") || "—";
  const Icon = sent ? Mail : MailX;
  const tone = sent
    ? { bg: "bg-[#DCFCE7]", text: "text-[#166534]", border: "border-[#86EFAC]", label: `Sent to ${toCount}` }
    : failedCount > 0
      ? { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", border: "border-[#FCA5A5]", label: "Bounced" }
      : { bg: "bg-[#F1F5F9]", text: "text-[#334155]", border: "border-[#E5E5E0]", label: "Filtered" };
  return (
    <details className={`rounded-xl border ${tone.border} bg-white`} open data-testid="maintenance-run-notification">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-[#0F172A] flex items-center gap-2">
        <Icon className={`w-4 h-4 ${tone.text}`} strokeWidth={1.75} />
        Notification
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tone.bg} ${tone.text}`} data-testid="maintenance-notify-status">
          {tone.label}
        </span>
        <span className="text-[10px] text-[#1E3A8A] uppercase tracking-wider ml-auto">
          Triggers: {triggers}
        </span>
      </summary>
      <div className="px-3 py-2 text-xs text-[#334155] space-y-2 border-t border-[#F0EFEB]">
        <div data-testid="maintenance-notify-reason">{notif.reason || "—"}</div>
        {toCount > 0 && (
          <div className="flex flex-wrap items-center gap-1" data-testid="maintenance-notify-to">
            <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A]">Sent to:</span>
            {(notif.to || []).map((email) => (
              <span key={email} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534]">{email}</span>
            ))}
          </div>
        )}
        {failedCount > 0 && (
          <div className="space-y-1" data-testid="maintenance-notify-failed">
            <div className="text-[10px] uppercase tracking-wider text-[#991B1B]">Bounced ({failedCount}):</div>
            {(notif.failed || []).map((f, i) => (
              <div key={`${f.email || "?"}-${i}`} className="text-[11px] px-2 py-1 rounded-lg bg-[#FEE2E2] text-[#991B1B]">
                <strong>{f.email || "—"}</strong>{f.error ? <> · {f.error}</> : null}
              </div>
            ))}
          </div>
        )}
        {notif.decided_at && (
          <div className="text-[10px] text-[#94A3B8]">Decided {formatRelative(notif.decided_at)}</div>
        )}
      </div>
    </details>
  );
}


function LastNotificationPill({ jobKey, last, onOpen }) {
  const sent = !!last.sent;
  const bounced = (last.failed_count || 0) > 0;
  const tone = sent
    ? bounced
      ? { bg: "bg-[#FEF3C7]", text: "text-[#92400E]", Icon: Mail, label: `Sent ${last.to_count} · ${last.failed_count} bounced` }
      : { bg: "bg-[#DCFCE7]", text: "text-[#166534]", Icon: Mail, label: `Sent to ${last.to_count}` }
    : bounced
      ? { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", Icon: MailX, label: `${last.failed_count} bounced` }
      : { bg: "bg-[#F1F5F9]", text: "text-[#334155]", Icon: MailX, label: "Filtered" };
  const Icon = tone.Icon;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`maintenance-last-notification-${jobKey}`}
      title={last.reason || "Open run details"}
      className={`-mt-1 inline-flex items-center gap-1.5 self-start text-[10px] px-2 py-0.5 rounded-full ${tone.bg} ${tone.text} hover:opacity-80 transition-opacity`}
    >
      <Icon className="w-3 h-3" strokeWidth={2} />
      <span className="uppercase tracking-wider font-medium">Last: {tone.label}</span>
      {last.started_at && (
        <span className="text-[9px] opacity-75">· {formatRelative(last.started_at)}</span>
      )}
    </button>
  );
}


function ITRecipientsDialog({ onClose }) {
  const [users, setUsers] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/maintenance/it-support-recipients");
      setUsers(data.recipients || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.patch(`/admin/maintenance/it-support-recipients/${u.id}`, {
        is_it_support: !u.is_it_support,
      });
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, is_it_support: data.is_it_support } : x));
      toast.success(data.is_it_support ? `Added ${u.name || u.email}` : `Removed ${u.name || u.email}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const filtered = (users || []).filter((u) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
  });
  const enabledCount = (users || []).filter((u) => u.is_it_support).length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-[#0F172A]/40 overflow-y-auto" data-testid="maintenance-recipients-dialog">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E0]">
          <div className="flex items-center gap-2.5">
            <UsersIcon className="w-5 h-5 text-[#1E3A8A]" strokeWidth={1.75} />
            <div>
              <h3 className="font-display text-lg font-semibold text-[#0F172A]">IT Support recipients</h3>
              <div className="text-xs text-[#334155]">
                Staff with <code className="px-1 py-0.5 rounded bg-[#F9F8F6] text-[10px]">is_it_support=true</code> receive maintenance notification emails.
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="maintenance-recipients-close" className="p-2 rounded-full hover:bg-[#F9F8F6]"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 border-b border-[#F0EFEB] flex items-center gap-3 flex-wrap">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="maintenance-recipients-search"
            className="flex-1 min-w-[200px] px-3 py-1.5 text-sm rounded-full border border-[#E5E5E0] bg-[#F9F8F6] focus:outline-none focus:border-[#1E3A8A]"
          />
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E0E7FF] text-[#1E3A8A]" data-testid="maintenance-recipients-count">
            {enabledCount} active
          </span>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {users === null ? (
            <div className="p-8 text-center text-[#1E3A8A]"><Loader2 className="w-5 h-5 animate-spin inline-block" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-[#334155] text-sm">No users match this search.</div>
          ) : (
            <ul className="divide-y divide-[#F0EFEB]">
              {filtered.map((u) => (
                <li key={u.id} data-testid={`maintenance-recipient-row-${u.id}`} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-[#F9F8F6]">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[#0F172A] text-sm truncate">{u.name || u.email}</div>
                    <div className="text-xs text-[#334155] truncate">{u.email}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#1E3A8A] mt-0.5">{u.role}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggle(u)}
                    disabled={busyId === u.id}
                    data-testid={`maintenance-recipient-toggle-${u.id}`}
                    aria-pressed={u.is_it_support}
                    title={u.is_it_support ? "Click to remove from IT Support recipients" : "Click to add as IT Support recipient"}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${u.is_it_support ? "bg-[#1E3A8A]" : "bg-[#CBD5E1]"}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${u.is_it_support ? "translate-x-6" : "translate-x-1"}`} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#E5E5E0] flex items-center justify-between text-[11px] text-[#334155]">
          <div>Changes take effect immediately. ADMIN_EMAIL fallback applies only when this list is empty.</div>
          <button onClick={onClose} className="px-4 py-1.5 text-xs rounded-full bg-[#1E3A8A] text-white hover:bg-[#172554]">Done</button>
        </div>
      </div>
    </div>
  );
}
